import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { sanityClient } from "@/sanity/client";
import { urlFor } from "@/sanity/imageUrl";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { canonicalLinks, routeMeta } from "@/lib/seo";

type GalleryImage = {
  _id: string;
  title?: string;
  caption?: string;
  image: { asset: { _ref: string } };
};

const QUERY = `*[_type == "galleryImage"] | order(order asc, _createdAt desc) {
  _id, title, caption, image
}`;

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      ...routeMeta("/gallery"),
      { title: "Winter Garden CPA Gallery | CCOB & Company" },
      { name: "description", content: "See CCOB & Company PA's Winter Garden office, team, client events, and community involvement throughout Central Florida." },
      { property: "og:title", content: "Gallery — CCOB And Company PA" },
      { property: "og:description", content: "A look inside our CPA firm." },
    ],
    links: canonicalLinks("/gallery"),
  }),
  component: GalleryPage,
});

function GalleryPage() {
  const { data, isLoading, error } = useQuery<GalleryImage[]>({
    queryKey: ["galleryImages"],
    queryFn: () => sanityClient.fetch<GalleryImage[]>(QUERY),
  });

  return (
    <>
      <PageHero eyebrow="Gallery" title="Inside CCOB And Company" subtitle="A look at our team, office, and the community we serve." image={img.heroAbout} />
      <section className="py-24">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-primary">Our firm in the community</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">This gallery shares moments from our Winter Garden office, professional events, and the Central Florida communities we serve. It reflects the personal, relationship-led approach behind our accounting, audit, tax, and advisory work.</p>
            <p className="mt-3 text-muted-foreground leading-relaxed">For more than three decades, clients have trusted our team for direct CPA guidance and practical financial clarity. Browse the latest photographs below, then contact us if you would like to meet the firm.</p>
          </div>
          {isLoading && <p className="text-muted-foreground">Loading gallery…</p>}
          {error && <p className="text-destructive">Could not load gallery. Make sure CORS is configured in Sanity.</p>}
          {data && data.length === 0 && (
            <p className="text-muted-foreground">No images yet. Add some in the <a href="/admin" className="text-primary underline">Studio</a>.</p>
          )}
          {data && data.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.map((g) => (
                <figure key={g._id} className="bg-card border border-border rounded-2xl overflow-hidden">
                  <img
                    src={urlFor(g.image).width(800).height(600).fit("crop").auto("format").url()}
                    srcSet={`${urlFor(g.image).width(400).height(300).fit("crop").auto("format").url()} 400w, ${urlFor(g.image).width(800).height(600).fit("crop").auto("format").url()} 800w, ${urlFor(g.image).width(1200).height(900).fit("crop").auto("format").url()} 1200w`}
                    sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
                    alt={g.title || g.caption || "Gallery image"}
                    width={800}
                    height={600}
                    loading="lazy"
                    decoding="async"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  {(g.title || g.caption) && (
                    <figcaption className="p-4">
                      {g.title && <div className="font-display text-lg text-primary">{g.title}</div>}
                      {g.caption && <div className="text-sm text-muted-foreground mt-1">{g.caption}</div>}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
